import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Row, Col, Select, Empty, Typography, Tooltip, Segmented, Button, Table, Tag, Tabs, notification, Dropdown, Collapse } from "antd";
import { BankOutlined, ClusterOutlined, EnvironmentOutlined, FilterOutlined, InfoCircleOutlined, LineChartOutlined, PieChartOutlined, ClearOutlined, UnorderedListOutlined, ReloadOutlined, FileTextOutlined, CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, SwapOutlined, DownloadOutlined, DownOutlined, RightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import lib from "../../assets/utils/lib";
import { SORT_BY, SORT_DIR, sorterToQuery, withServerSorter } from "../../utils/tableSortParams";
import { tatFromRecord } from "../../utils/turnaroundTime";
import {
  dateRangeFilterDropdown,
  numericRangeFilterDropdown,
  parseTableRangeFilter,
  rangeFilterValue,
} from "./paQueueTableUtils";
import ConfigurableRangePicker from "../compoinents/ConfigurableRangePicker";
import DebounceSelect from "../compoinents/DebouncedSelect";
import DashboardHero from "../dashboard/components/DashboardHero";
import DashboardSection from "../dashboard/components/DashboardSection";
import FinanceReconciliationPanel from "../../components/Finance/FinanceReconciliationPanel";
import FinanceHelpPanel from "../../components/Finance/FinanceHelpPanel";
import FieldGuideLabel from "../../components/Finance/FieldGuideLabel";
import { RowListSkeleton, TableSkeleton } from "../dashboard/components/DashboardSkeleton";
import { formatCompactCurrency, hasMetricValue } from "../dashboard/utils";
import { getPaEncounterScopeDisplay, mergePaFieldGuides } from "./paAnalyticsHelpers";
import PACostTrendChart from "./PACostTrendChart";
import PATurnaroundSection from "./PATurnaroundSection";
import PAItemsDrawer, { signedMoney } from "./PAItemsDrawer";
import {
  getFieldGuideHelpText,
  getFieldGuideHowToUse,
  getFieldGuideLabel,
  getFinanceHelpBlock,
  getHelpSectionTitle,
  getHelpSectionTooltip,
  getReconciliationBlock,
  getStatusLabelHelp,
  isFieldGuidePrimary,
  mergeFinanceHelp,
  normalizePaymentAdviceReconcileParams,
  paDashboardDatesToPaymentAdvicePeriod,
  parsePaAnalyticsListPayload,
  resolveOrganizationPaymentAdviceUrl,
  resolveOrganizationById,
  buildOrganizationRouteHash,
  storePaymentAdviceReconcileParams,
} from "../../utils/financeReconciliationHelpers";
import "../../components/Finance/finance-reconciliation.css";
import "../dashboard/index.css";
import "./pa-dashboard.css";

const { Text } = Typography;

const DEFAULT_INTERVAL = "month";
const DEFAULT_DATE_TYPE = "request_date";

function buildEncountersListContextKey(commonParams, scope, sort, colFilters) {
  return JSON.stringify({ p: commonParams, scope, sort, colFilters });
}

function parseEncountersListBody(body) {
  if (!body) {
    return { rows: [], total: 0 };
  }
  if (body.error != null && Number(body.error) !== 0) {
    return { rows: [], total: 0 };
  }
  const root = body.data;
  let rows = [];
  let pagination = null;
  if (Array.isArray(root)) {
    rows = root;
  } else if (root && Array.isArray(root.items)) {
    rows = root.items;
    pagination = root.pagination || root.meta;
  } else if (root && Array.isArray(root.data)) {
    rows = root.data;
    pagination = root.pagination || root.meta;
  } else if (root && Array.isArray(root.encounters)) {
    rows = root.encounters;
    pagination = root.pagination || root.meta;
  }
  const total =
    pagination?.totalCount ??
    pagination?.total ??
    pagination?.total_items ??
    pagination?.total_records ??
    rows.length;
  return { rows, total: Number(total) || 0 };
}

function normalizeEncounterRow(raw, index) {
  const consumer = raw.consumer && typeof raw.consumer === "object" ? raw.consumer : {};
  const code =
    raw.code ?? raw.checkin_id ?? raw.checkin_code ?? raw.encounter_code ?? raw.id;
  return {
    ...raw,
    key: code || `enc-${index}`,
    code,
    insurance_no: consumer.insurance_no ?? raw.insurance_no,
    first_name: consumer.first_name ?? raw.first_name,
    surname: consumer.surname ?? raw.surname,
  };
}

const AGGREGATED_STATUS_TAG = {
  Approved: "success",
  Pending: "warning",
  Declined: "error",
  Queried: "processing",
  Rejected: "error",
};

function paStatusLabel(status) {
  return status === "Declined" ? "Rejected" : status;
}

/** Item-based status breakdown: colours and which rows open a PA line drill-down. */
const STATUS_BAR_COLORS = {
  Approved: "#22c55e",
  Pending: "#f59e0b",
  Rejected: "#ef4444",
  Variance: "#8b5cf6",
};

const STATUS_AMOUNT_LABEL = {
  Approved: "Approved amount",
  Pending: "Requested, awaiting decision",
  Rejected: "Requested, not approved",
  Variance: "Net variance",
};

function breakdownItemLabel(item) {
  return item?.label || item?.name || (item?.key != null && item.key !== "" ? String(item.key) : "—");
}

function getDefaultRangeForInterval(interval) {
  const now = dayjs();
  switch (interval) {
    case "day":
      return { start: now.startOf("day"), end: now.endOf("day") };
    case "week":
      return { start: now.startOf("week"), end: now.endOf("week") };
    case "month":
      return { start: now.startOf("month"), end: now.endOf("month") };
    case "year":
      return { start: now.startOf("year"), end: now.endOf("year") };
    default:
      return { start: now.startOf("month"), end: now.endOf("month") };
  }
}

const PADashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [timeSeries, setTimeSeries] = useState([]);
  const [timeSeriesMeta, setTimeSeriesMeta] = useState({ field_guide: [], help: null, totals: null });
  const [breakdown, setBreakdown] = useState([]);
  const [breakdownMeta, setBreakdownMeta] = useState({ field_guide: [], help: null, totals: null });

  const [encounters, setEncounters] = useState([]);
  const [encountersLoading, setEncountersLoading] = useState(false);
  const [encountersPage, setEncountersPage] = useState(1);
  const [encountersPageSize, setEncountersPageSize] = useState(20);
  const [encountersTotal, setEncountersTotal] = useState(0);
  /** Backend may support scope; harmless if ignored. */
  const [encountersScope, setEncountersScope] = useState("in_scope");
  const [encountersSortBy, setEncountersSortBy] = useState(SORT_BY.CHECKIN_DATE);
  const [encountersSortDir, setEncountersSortDir] = useState(SORT_DIR.DESC);
  const [encountersColFilters, setEncountersColFilters] = useState({
    itemsMin: undefined,
    itemsMax: undefined,
    requestedMin: undefined,
    requestedMax: undefined,
    approvedMin: undefined,
    approvedMax: undefined,
  });
  /** PA line drill-down drawer: "variance" | "rejected" | null. */
  const [itemsDrawer, setItemsDrawer] = useState(null);
  /** Cost breakdown vs encounter list — encounters API runs only when this tab is active. */
  const [detailTab, setDetailTab] = useState("breakdown");

  const [dateRange, setDateRange] = useState(null);
  /** Main-filter staff selector: applies to the PA turnaround section only. */
  const [tatStaffId, setTatStaffId] = useState(null);
  const [tatStaffOptions, setTatStaffOptions] = useState([]);
  const handleTatStaffOptions = useCallback((options) => setTatStaffOptions(options || []), []);

  const [filters, setFilters] = useState({
    org_id: null,
    /** Single plan id (backward compatible API param `plan_type`) */
    plan_type: null,
    /** Plan category: `plans_types.plans_types_id` → query `plan_type_id` */
    plan_type_id: null,
    /** Specific plan ids → query `plan_ids` (comma-separated) */
    plan_ids: [],
    location_id: null,
    status: null,
    checkin_type_ids: null,
    date_type: DEFAULT_DATE_TYPE,
    interval: DEFAULT_INTERVAL,
    group_by: "organization",
  });

  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  const [loadingTimeSeries, setLoadingTimeSeries] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(() => new Date());

  const [breakdownMode, setBreakdownMode] = useState("organization");
  const reconcileSignatureRef = useRef("");
  const [orgSelectOption, setOrgSelectOption] = useState(null);
  const [planSelectOption, setPlanSelectOption] = useState(null);
  const [facilitySelectOption, setFacilitySelectOption] = useState(null);

  /** Cached GET /v2/admin/plans?scope=active list (no server-side search on catalog). */
  const adminPlansCatalogRef = useRef(null);

  const effectiveRange = useMemo(() => {
    const hasRange = dateRange && (
      (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) ||
      (dateRange?.start && dateRange?.end)
    );
    if (hasRange) {
      const start = Array.isArray(dateRange) ? dateRange[0] : dateRange.start;
      const end = Array.isArray(dateRange) ? dateRange[1] : dateRange.end;
      return {
        start: dayjs.isDayjs(start) ? start : dayjs(start),
        end: dayjs.isDayjs(end) ? end : dayjs(end),
      };
    }
    return getDefaultRangeForInterval(filters.interval || DEFAULT_INTERVAL);
  }, [dateRange, filters.interval]);

  const commonParams = useMemo(() => {
    const planIds =
      Array.isArray(filters.plan_ids) && filters.plan_ids.length
        ? filters.plan_ids.join(",")
        : undefined;
    return {
      date_from: effectiveRange.start ? effectiveRange.start.startOf("day").toISOString() : null,
      date_to: effectiveRange.end ? effectiveRange.end.endOf("day").toISOString() : null,
      date_type: filters.date_type,
      org_id: filters.org_id || undefined,
      plan_type: filters.plan_type || undefined,
      plan_type_id: filters.plan_type_id || undefined,
      plan_ids: planIds,
      location_id: filters.location_id || undefined,
      status: filters.status || undefined,
      checkin_type_ids: filters.checkin_type_ids || undefined,
    };
  }, [effectiveRange, filters]);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await lib.getPAAnalyticsSummary(commonParams);
      const body = resp?.data;
      if (body && !body.error) {
        setSummary(body.data || null);
      } else {
        setSummary(null);
      }
    } catch (e) {
      console.error("Error loading PA summary", e);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [commonParams]);

  const loadTimeSeries = useCallback(async () => {
    setLoadingTimeSeries(true);
    try {
      const resp = await lib.getPAAnalyticsTimeSeries({
        ...commonParams,
        interval: filters.interval || DEFAULT_INTERVAL,
      });
      const body = resp?.data;
      if (body && !body.error) {
        const parsed = parsePaAnalyticsListPayload(body);
        setTimeSeries(parsed.rows);
        setTimeSeriesMeta({
          field_guide: parsed.field_guide,
          help: parsed.help,
          totals: parsed.totals,
        });
      } else {
        setTimeSeries([]);
        setTimeSeriesMeta({ field_guide: [], help: null, totals: null });
      }
    } catch (e) {
      console.error("Error loading PA time-series", e);
      setTimeSeries([]);
      setTimeSeriesMeta({ field_guide: [], help: null, totals: null });
    } finally {
      setLoadingTimeSeries(false);
    }
  }, [commonParams, filters.interval]);

  const loadBreakdown = useCallback(async () => {
    setLoadingBreakdown(true);
    try {
      const resp = await lib.getPAAnalyticsBreakdown({
        ...commonParams,
        group_by: breakdownMode,
      });
      const body = resp?.data;
      if (body && !body.error) {
        const parsed = parsePaAnalyticsListPayload(body);
        setBreakdown(parsed.rows);
        setBreakdownMeta({
          field_guide: parsed.field_guide,
          help: parsed.help,
          totals: parsed.totals,
        });
      } else {
        setBreakdown([]);
        setBreakdownMeta({ field_guide: [], help: null, totals: null });
      }
    } catch (e) {
      console.error("Error loading PA breakdown", e);
      setBreakdown([]);
      setBreakdownMeta({ field_guide: [], help: null, totals: null });
    } finally {
      setLoadingBreakdown(false);
    }
  }, [commonParams, breakdownMode]);

  const encountersListCtxRef = useRef("");

  const loadEncounters = useCallback(async () => {
    const ctxKey = buildEncountersListContextKey(
      commonParams,
      encountersScope,
      { sort_by: encountersSortBy, sort_dir: encountersSortDir },
      encountersColFilters,
    );
    let pageToFetch = encountersPage;
    if (encountersListCtxRef.current !== ctxKey) {
      encountersListCtxRef.current = ctxKey;
      pageToFetch = 1;
      if (encountersPage !== 1) {
        setEncountersPage(1);
      }
    }
    setEncountersLoading(true);
    try {
      const resp = await lib.getPAAnalyticsEncounters({
        ...commonParams,
        page: pageToFetch,
        pageSize: encountersPageSize,
        scope: encountersScope,
        sort_by: encountersSortBy,
        sort_dir: encountersSortDir,
        items_min: encountersColFilters.itemsMin,
        items_max: encountersColFilters.itemsMax,
        requested_min: encountersColFilters.requestedMin,
        requested_max: encountersColFilters.requestedMax,
        approved_min: encountersColFilters.approvedMin,
        approved_max: encountersColFilters.approvedMax,
      });
      const body = resp?.data;
      const { rows, total } = parseEncountersListBody(body);
      setEncounters(rows.map((r, i) => normalizeEncounterRow(r, i)));
      setEncountersTotal(total);
    } catch (e) {
      console.error("Error loading PA analytics encounters", e);
      setEncounters([]);
      setEncountersTotal(0);
    } finally {
      setEncountersLoading(false);
    }
  }, [commonParams, encountersPage, encountersPageSize, encountersScope, encountersSortBy, encountersSortDir, encountersColFilters]);

  useEffect(() => {
    loadSummary();
    loadTimeSeries();
    loadBreakdown();
  }, [loadSummary, loadTimeSeries, loadBreakdown]);

  const openPaAnalyticsTab = location.state?.openPaAnalyticsDetailTab;
  useEffect(() => {
    if (openPaAnalyticsTab !== "encounters" && openPaAnalyticsTab !== "breakdown") return;
    setDetailTab(openPaAnalyticsTab);
    navigate(location.pathname, { replace: true, state: {} });
  }, [openPaAnalyticsTab, location.pathname, navigate]);

  useEffect(() => {
    const signature = searchParams.toString();
    if (!signature || reconcileSignatureRef.current === signature) return;

    const orgId = searchParams.get("org_id") || searchParams.get("organization_id");
    const dateFrom = searchParams.get("date_from");
    const dateTo = searchParams.get("date_to");
    const dateType = searchParams.get("date_type");
    const status = searchParams.get("status");
    const locationId = searchParams.get("location_id");
    const planTypeId = searchParams.get("plan_type_id");
    const planIds = searchParams.get("plan_ids");
    const checkinTypeIds = searchParams.get("checkin_type_ids");

    if (!orgId && !dateFrom && !dateTo && !status && !locationId && !planTypeId && !planIds) {
      return;
    }

    reconcileSignatureRef.current = signature;
    setShowFilters(true);
    setFilters((prev) => ({
      ...prev,
      org_id: orgId ? Number(orgId) || orgId : prev.org_id,
      date_type: dateType || prev.date_type,
      status: status || prev.status,
      location_id: locationId ? Number(locationId) || locationId : prev.location_id,
      plan_type_id: planTypeId ? Number(planTypeId) || planTypeId : prev.plan_type_id,
      plan_ids: planIds
        ? planIds.split(",").map((id) => Number(id) || id).filter(Boolean)
        : prev.plan_ids,
      checkin_type_ids: checkinTypeIds || prev.checkin_type_ids,
    }));
    if (dateFrom && dateTo) {
      const start = dayjs(dateFrom);
      const end = dayjs(dateTo);
      if (start.isValid() && end.isValid()) {
        setDateRange([start, end]);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    const orgId = filters.org_id;
    if (!orgId) {
      setOrgSelectOption(null);
      return;
    }
    if (orgSelectOption?.value != null && String(orgSelectOption.value) === String(orgId) && orgSelectOption?.hash) {
      return;
    }

    let cancelled = false;
    (async () => {
      const match = await resolveOrganizationById(orgId);
      if (cancelled) return;
      if (match) {
        setOrgSelectOption({
          label: match.org_name || match.name || `Organization #${orgId}`,
          value: match.org_id ?? match.id ?? orgId,
          hash: buildOrganizationRouteHash(match),
        });
        return;
      }
      setOrgSelectOption({ label: `Organization #${orgId}`, value: orgId, hash: null });
    })();

    return () => {
      cancelled = true;
    };
  }, [filters.org_id]);

  useEffect(() => {
    if (detailTab !== "encounters") return;
    loadEncounters();
  }, [detailTab, loadEncounters]);

  const onDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  const onIntervalChange = (value) => {
    setDateRange(null);
    setFilters((prev) => ({ ...prev, interval: value }));
  };

  const onDateTypeChange = (value) => {
    setFilters((prev) => ({ ...prev, date_type: value }));
  };

  const onBreakdownModeChange = (value) => {
    setBreakdownMode(value);
  };

  const onBreakdownRowClick = (item) => {
    const key = item?.key ?? null;
    // "__unattributed__" is a real bucket (kept so totals reconcile), not a drillable
    // org/plan/facility — there is nothing behind it to filter into.
    if (key == null || key === "" || key === "__unattributed__") return;
    const label = breakdownItemLabel(item);

    if (breakdownMode === "plan_type") {
      setPlanSelectOption({ label, value: key });
      setOrgSelectOption(null);
      setFacilitySelectOption(null);
      setFilters((prev) => ({ ...prev, plan_type: key, org_id: null, location_id: null }));
      setBreakdownMode("organization");
      setDetailTab("breakdown");
      return;
    }

    if (breakdownMode === "organization") {
      setOrgSelectOption({ label, value: key, hash: null });
      setFacilitySelectOption(null);
      setFilters((prev) => ({ ...prev, org_id: key, location_id: null }));
      setBreakdownMode("location");
      setDetailTab("breakdown");
      return;
    }

    if (breakdownMode === "location") {
      setFacilitySelectOption({ label, value: key });
      setFilters((prev) => ({ ...prev, location_id: key }));
      setDetailTab("encounters");
    }
  };

  const peelQueueContext = (level) => {
    if (level === "all") {
      setPlanSelectOption(null);
      setOrgSelectOption(null);
      setFacilitySelectOption(null);
      setFilters((prev) => ({ ...prev, plan_type: null, org_id: null, location_id: null }));
      return;
    }
    if (level === "plan") {
      setOrgSelectOption(null);
      setFacilitySelectOption(null);
      setFilters((prev) => ({ ...prev, org_id: null, location_id: null }));
      setBreakdownMode("organization");
      setDetailTab("breakdown");
      return;
    }
    if (level === "org") {
      setFacilitySelectOption(null);
      setFilters((prev) => ({ ...prev, location_id: null }));
      setBreakdownMode("location");
      setDetailTab("breakdown");
      return;
    }
    if (level === "facility") {
      setDetailTab("encounters");
    }
  };

  const resetFilters = () => {
    setDateRange(null);
    setTatStaffId(null);
    setPlanSelectOption(null);
    setOrgSelectOption(null);
    setFacilitySelectOption(null);
    setFilters((prev) => ({
      org_id: null,
      plan_type: null,
      plan_type_id: null,
      plan_ids: [],
      location_id: null,
      status: null,
      date_type: DEFAULT_DATE_TYPE,
      interval: prev.interval || DEFAULT_INTERVAL,
      group_by: prev.group_by || "organization",
    }));
  };

  const hasActiveFilters =
    filters.org_id ||
    filters.plan_type ||
    filters.plan_type_id ||
    (Array.isArray(filters.plan_ids) && filters.plan_ids.length > 0) ||
    filters.location_id ||
    filters.status;

  const currency = (value) => {
    return `₦${Number(value || 0).toLocaleString()}`;
  };

  const percentage = (value) => {
    if (!value && value !== 0) return "0%";
    return `${Number(value).toFixed(1)}%`;
  };

  const summaryData = summary || {};
  const fieldGuide = useMemo(
    () => (Array.isArray(summary?.field_guide) ? summary.field_guide : []),
    [summary],
  );
  const statusFieldGuide = useMemo(
    () => (Array.isArray(summary?.status_breakdown_field_guide)
      ? summary.status_breakdown_field_guide
      : []),
    [summary],
  );
  const financeHelp = useMemo(
    () => mergeFinanceHelp(
      getFinanceHelpBlock(summary),
      mergeFinanceHelp(timeSeriesMeta.help, breakdownMeta.help),
    ),
    [summary, timeSeriesMeta.help, breakdownMeta.help],
  );
  const reconciliation = getReconciliationBlock(summaryData);
  const trendFieldGuide = useMemo(
    () => mergePaFieldGuides(timeSeriesMeta.field_guide, fieldGuide),
    [timeSeriesMeta.field_guide, fieldGuide],
  );
  const costBreakdownFieldGuide = useMemo(
    () => mergePaFieldGuides(breakdownMeta.field_guide, fieldGuide),
    [breakdownMeta.field_guide, fieldGuide],
  );

  const requestedCostLabel = getFieldGuideLabel(fieldGuide, "total_requested_cost", "Requested");
  const approvedCostLabel = getFieldGuideLabel(fieldGuide, "total_approved_cost", "Total approved cost");
  const requestedCostHelp = getFieldGuideHelpText(fieldGuide, "total_requested_cost");
  const approvedCostHelp = getFieldGuideHelpText(fieldGuide, "total_approved_cost");
  const requestedCostHowTo = getFieldGuideHowToUse(fieldGuide, "total_requested_cost");
  const approvedCostHowTo = getFieldGuideHowToUse(fieldGuide, "total_approved_cost");

  const statusBreakdown = useMemo(() => {
    const rows = Array.isArray(summaryData.status_breakdown)
      ? summaryData.status_breakdown
      : [];
    return {
      rows,
      /** Approved / Pending / Rejected make up the whole; Variance sits inside Approved. */
      parts: rows.filter((row) => row.status !== "Variance"),
      totalItems: Number(summaryData.total_pa_items) || 0,
    };
  }, [summaryData.status_breakdown, summaryData.total_pa_items]);

  /** Authoritative period totals — same source as snapshot KPIs (not sum of trend buckets). */
  const periodApprovedTotal =
    summaryData.total_approved_cost
    ?? timeSeriesMeta.totals?.total_approved_cost
    ?? null;
  const periodRequestedTotal =
    summaryData.total_requested_cost
    ?? timeSeriesMeta.totals?.total_requested_cost
    ?? null;

  const trendBuckets = useMemo(() => {
    if (!Array.isArray(timeSeries) || !timeSeries.length) return [];
    const usePeriodTotals = timeSeries.length === 1;
    return timeSeries.map((bucket, idx) => {
      const requested = usePeriodTotals && periodRequestedTotal != null
        ? periodRequestedTotal
        : bucket.total_requested_cost;
      const approved = usePeriodTotals && periodApprovedTotal != null
        ? periodApprovedTotal
        : bucket.total_approved_cost;
      const label = bucket.period_label
        || (bucket.period_start
          ? dayjs(bucket.period_start).format(timeSeries.length > 14 ? "MMM D" : "MMM D, YYYY")
          : `Period ${idx + 1}`);
      return {
        key: bucket.period_start || `bucket-${idx}`,
        label,
        requested: Number(requested) || 0,
        approved: Number(approved) || 0,
        pending: Number(bucket.total_pending_cost) || 0,
        rejected: Number(bucket.total_rejected_cost) || 0,
        variance: Number(bucket.variance_net) || 0,
        items: Number(bucket.total_pa_items) || 0,
        encounters: Number(bucket.total_requests) || 0,
      };
    });
  }, [timeSeries, periodRequestedTotal, periodApprovedTotal]);

  const trendChartData = useMemo(() => ({
    labels: trendBuckets.map((b) => b.label),
    requested: trendBuckets.map((b) => b.requested),
    approved: trendBuckets.map((b) => b.approved),
    pending: trendBuckets.map((b) => b.pending),
    rejected: trendBuckets.map((b) => b.rejected),
    variance: trendBuckets.map((b) => b.variance),
    items: trendBuckets.map((b) => b.items),
    encounters: trendBuckets.map((b) => b.encounters),
  }), [trendBuckets]);

  const trendBreakdownLabel = useMemo(() => {
    const interval = filters.interval || DEFAULT_INTERVAL;
    const unit =
      interval === "week" ? "week"
        : interval === "month" ? "month"
          : interval === "year" ? "year"
            : "day";
    const title =
      interval === "week" ? "Weekly breakdown"
        : interval === "month" ? "Monthly breakdown"
          : interval === "year" ? "Yearly breakdown"
            : "Daily breakdown";
    const count = trendBuckets.length;
    const plural = count === 1 ? unit : `${unit}s`;
    return `${title} (${count} ${plural})`;
  }, [filters.interval, trendBuckets.length]);

  const hasOrgInScope = Boolean(filters.org_id);
  const hasPlanInScope = Boolean(filters.plan_type);
  const hasFacilityInScope = Boolean(filters.location_id);

  const buildPreauthQueuePath = useCallback(() => {
    const params = new URLSearchParams();
    if (filters.location_id) {
      params.set("facility_id", String(filters.location_id));
      params.set("location_id", String(filters.location_id));
    }
    if (filters.org_id) params.set("org_id", String(filters.org_id));
    if (filters.status) params.set("s", String(filters.status));
    if (effectiveRange?.start) params.set("from", effectiveRange.start.format("YYYY-MM-DD"));
    if (effectiveRange?.end) params.set("to", effectiveRange.end.format("YYYY-MM-DD"));
    if (filters.checkin_type_ids) params.set("type", String(filters.checkin_type_ids));
    const qs = params.toString();
    return qs ? `/preauth?${qs}` : "/preauth";
  }, [filters.location_id, filters.org_id, filters.status, filters.checkin_type_ids, effectiveRange]);

  const queueContextCrumbs = useMemo(() => {
    const crumbs = [{ key: "all", label: "All", level: "all" }];
    if (filters.plan_type) {
      crumbs.push({
        key: "plan",
        label: planSelectOption?.label || `Plan #${filters.plan_type}`,
        level: "plan",
      });
    }
    if (filters.org_id) {
      crumbs.push({
        key: "org",
        label: orgSelectOption?.label || `Organization #${filters.org_id}`,
        level: "org",
      });
    }
    if (filters.location_id) {
      crumbs.push({
        key: "facility",
        label: facilitySelectOption?.label || `Facility #${filters.location_id}`,
        level: "facility",
      });
    }
    return crumbs;
  }, [
    filters.plan_type,
    filters.org_id,
    filters.location_id,
    planSelectOption,
    orgSelectOption,
    facilitySelectOption,
  ]);

  const hasQueueContext = queueContextCrumbs.length > 1;

  const queueContextHint = useMemo(() => {
    if (detailTab === "encounters" && filters.location_id) {
      return "Encounters in this facility slice";
    }
    if (detailTab === "breakdown" && breakdownMode === "organization" && filters.plan_type) {
      return "Organizations that consume this plan";
    }
    if (detailTab === "breakdown" && breakdownMode === "location" && (filters.org_id || filters.plan_type)) {
      return "Facilities where this consumption happened";
    }
    if (detailTab === "breakdown" && breakdownMode === "plan_type") {
      return "Click a plan to see organizations that consume it";
    }
    if (detailTab === "breakdown" && breakdownMode === "organization") {
      return "Click an organization to see facilities that consumed it";
    }
    if (detailTab === "breakdown" && breakdownMode === "location") {
      return "Click a facility to see encounters in this slice";
    }
    return null;
  }, [detailTab, breakdownMode, filters.location_id, filters.org_id, filters.plan_type]);

  const breakdownDrillHint =
    breakdownMode === "plan_type"
      ? "View organizations"
      : breakdownMode === "organization"
        ? "View facilities"
        : "View encounters";

  const openPaymentAdviceCompare = useCallback(async () => {
    const orgId = filters.org_id;
    if (!orgId) return;

    const dateFrom = effectiveRange?.start?.format?.("YYYY-MM-DD");
    const dateTo = effectiveRange?.end?.format?.("YYYY-MM-DD");
    const period = paDashboardDatesToPaymentAdvicePeriod(dateFrom, dateTo);

    const stored = normalizePaymentAdviceReconcileParams(
      reconciliation?.equivalent_payment_advice_params,
      {
        organizationId: orgId,
        period,
        sharedFilters: reconciliation?.shared_filters,
        reportOptions: {
          data_scope: "pa_approved",
          pa_line_statuses: ["approved"],
        },
      },
    );

    storePaymentAdviceReconcileParams(stored);

    const hash = orgSelectOption?.hash
      && String(orgSelectOption.value) === String(orgId)
      ? orgSelectOption.hash
      : null;

    if (hash) {
      navigate(`/corporations/users/${encodeURIComponent(hash)}?tab=payment_advice`);
      return;
    }

    const url = await resolveOrganizationPaymentAdviceUrl(orgId);
    if (url) {
      navigate(url);
      return;
    }
    notification.error({
      message: "Could not open utilization reports",
      description: "Organization route could not be resolved. Open the organization manually and use Utilization reports.",
    });
  }, [navigate, reconciliation, filters.org_id, effectiveRange, orgSelectOption]);

  const dateRangeLabel = useMemo(() => {
    if (!effectiveRange?.start?.isValid?.() || !effectiveRange?.end?.isValid?.()) return null;
    if (effectiveRange.start.isSame(effectiveRange.end, "day")) {
      return effectiveRange.start.format("MMM D, YYYY");
    }
    return `${effectiveRange.start.format("MMM D")} – ${effectiveRange.end.format("MMM D, YYYY")}`;
  }, [effectiveRange]);

  const heroMetrics = useMemo(() => {
    if (!summary) return [];
    const d = summary;
    const encounterScope = getPaEncounterScopeDisplay(d);
    const goPreauth = () => navigate(buildPreauthQueuePath());
    const guide = (field, fallback) => ({
      title: getFieldGuideLabel(fieldGuide, field, fallback),
      helpText: getFieldGuideHelpText(fieldGuide, field),
      howToUse: getFieldGuideHowToUse(fieldGuide, field),
    });
    const itemCount = (n) => `${Number(n || 0).toLocaleString()} item${Number(n) === 1 ? "" : "s"}`;
    return [
      {
        key: "pa-items",
        ...guide("total_pa_items", "Total PA items requested"),
        value: d.total_pa_items,
        color: "#722ed1",
        icon: <UnorderedListOutlined />,
        subtitle: `${encounterScope.withPA.toLocaleString()} encounters with PA`,
        primary: true,
        onClick: goPreauth,
      },
      {
        key: "pa-requested",
        ...guide("total_requested_cost", "Total requested cost"),
        value: d.total_requested_cost,
        prefix: "₦",
        compact: true,
        color: "#3497D3",
        icon: <FileTextOutlined />,
        subtitle: "All PA lines",
        onClick: goPreauth,
      },
      {
        key: "pa-approved",
        ...guide("total_approved_cost", "Total approved cost"),
        value: d.total_approved_cost,
        prefix: "₦",
        compact: true,
        color: "#49B870",
        icon: <CheckCircleOutlined />,
        subtitle: itemCount(d.approved_items),
        primary: true,
        onClick: goPreauth,
      },
      {
        key: "pa-pending",
        ...guide("total_pending_cost", "Pending cost"),
        value: d.total_pending_cost,
        prefix: "₦",
        compact: true,
        color: "#f59e0b",
        icon: <ClockCircleOutlined />,
        subtitle: itemCount(d.pending_items),
        onClick: goPreauth,
      },
      {
        key: "pa-rejected",
        ...guide("total_rejected_cost", "Rejected cost"),
        value: d.total_rejected_cost,
        prefix: "₦",
        compact: true,
        color: "#ef4444",
        icon: <CloseCircleOutlined />,
        subtitle: `${itemCount(d.rejected_items)} · view lines`,
        onClick: () => setItemsDrawer("rejected"),
      },
      {
        key: "pa-variance",
        ...guide("variance_net", "PA variance"),
        value: d.variance_net,
        formatValue: (v) => signedMoney(v, formatCompactCurrency),
        color: "#8b5cf6",
        icon: <SwapOutlined />,
        subtitle: `${itemCount(d.variance_items)} · −${formatCompactCurrency(d.variance_reductions)} / +${formatCompactCurrency(d.variance_increases)}`,
        onClick: () => setItemsDrawer("variance"),
      },
    ].filter((m) => hasMetricValue(m.value));
  }, [summary, navigate, fieldGuide, buildPreauthQueuePath]);

  const encounterScopeHint = useMemo(() => {
    if (!summary) return null;
    return getPaEncounterScopeDisplay(summary).hint;
  }, [summary]);

  const refreshAll = useCallback(() => {
    loadSummary();
    loadTimeSeries();
    loadBreakdown();
    if (detailTab === "encounters") loadEncounters();
    setLastRefreshed(new Date());
  }, [loadSummary, loadTimeSeries, loadBreakdown, detailTab, loadEncounters]);

  const handleDownload = useCallback(async (dataset) => {
    setExporting(true);
    try {
      const result = await lib.downloadPAAnalyticsExport({
        ...commonParams,
        dataset,
        group_by: dataset === "breakdown" ? breakdownMode : undefined,
        sort_by: dataset === "encounters" ? encountersSortBy : undefined,
        sort_dir: dataset === "encounters" ? encountersSortDir : undefined,
        items_min: dataset === "encounters" ? encountersColFilters.itemsMin : undefined,
        items_max: dataset === "encounters" ? encountersColFilters.itemsMax : undefined,
        requested_min: dataset === "encounters" ? encountersColFilters.requestedMin : undefined,
        requested_max: dataset === "encounters" ? encountersColFilters.requestedMax : undefined,
        approved_min: dataset === "encounters" ? encountersColFilters.approvedMin : undefined,
        approved_max: dataset === "encounters" ? encountersColFilters.approvedMax : undefined,
      });
      notification.success({
        message: "Download started",
        description: result?.filename || "CSV export ready",
        placement: "topRight",
      });
    } catch (e) {
      notification.error({
        message: "Download failed",
        description: e?.message || "Could not export PA analytics",
        placement: "topRight",
      });
    } finally {
      setExporting(false);
    }
  }, [commonParams, breakdownMode, encountersSortBy, encountersSortDir, encountersColFilters]);

  const downloadMenuItems = useMemo(() => ([
    {
      key: "encounters",
      label: "Encounters CSV",
      onClick: () => handleDownload("encounters"),
    },
    {
      key: "variance",
      label: "Variance lines CSV",
      onClick: () => handleDownload("variance"),
    },
    {
      key: "rejected",
      label: "Rejected lines CSV",
      onClick: () => handleDownload("rejected"),
    },
    {
      key: "breakdown",
      label: `Cost breakdown CSV (${breakdownMode === "plan_type" ? "plan" : breakdownMode === "location" ? "facility" : "organization"})`,
      onClick: () => handleDownload("breakdown"),
    },
  ]), [handleDownload, breakdownMode]);

  const lastUpdated = lastRefreshed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const handleOrgSearch = async (search) => {
    const resp = await lib.getOrganizationsV2({ mode: "mini", search, limit: 20, page: 1 });
    const parsed = lib.parseApiEnvelope(resp);
    if (!parsed.ok || !Array.isArray(parsed.data)) return [];
    return parsed.data.map((org) => ({
      label: org.org_name || org.name,
      value: org.org_id ?? org.id,
      hash: buildOrganizationRouteHash(org),
    }));
  };

  const handlePlanTypeSearch = useCallback(async (search) => {
    // GET /v2/admin/plans — data.plans[]; filter by plan name / type client-side (no q= param).
    if (!adminPlansCatalogRef.current) {
      const resp = await lib.getAdminPlansCatalog({ scope: "active" });
      const body = resp?.data;
      if (body?.error || !body?.data?.plans) {
        return [];
      }
      adminPlansCatalogRef.current = body.data.plans;
    }
    const q = (search || "").trim().toLowerCase();
    let list = adminPlansCatalogRef.current;
    if (q) {
      list = list.filter(
        (p) =>
          (p.plan_name || "").toLowerCase().includes(q) ||
          (p.plan_type_name || "").toLowerCase().includes(q) ||
          String(p.plan_id || "").includes(q)
      );
    }
    return list
      .filter((plan) => plan.plan_type_id != null || plan.plan_type != null)
      .map((plan) => ({
        label: plan.plan_type_name
          ? `${plan.plan_name} (${plan.plan_type_name})`
          : plan.plan_name || `Plan ${plan.plan_id}`,
        // PA analytics expects plan_type (FK); prefer joined plan_type_id, else raw plan_type on row
        value: plan.plan_type_id ?? plan.plan_type,
      }));
  }, []);

  /** Facility search via GET /v2/search?resource=facilities */
  const handleFacilitySearch = async (search) => {
    const q = (search || "").trim();
    if (q.length < 3) return [];
    try {
      const resp = await lib.unifiedSearch({
        resource: "facilities",
        search: q,
        page: 1,
        pageSize: 50,
      });
      const body = resp?.data;
      if (body && Number(body?.error) === 0) {
        return (body.data || []).map((loc) => ({
          label: loc.name || loc.facility_name,
          value: loc.id || loc.facility_id,
        }));
      }
    } catch {
      /* ignore */
    }
    return [];
  };

  /** @deprecated use handleFacilitySearch — alias for older references / hot-reload */
  const handleLocationSearch = handleFacilitySearch;

  const formatEncounterDate = (value) => {
    if (!value) return "—";
    const d = dayjs(value);
    return d.isValid() ? d.format("MMM D, YYYY HH:mm") : String(value);
  };

  const encounterColumns = [
    // {
    //   title: "Encounter",
    //   dataIndex: "checkin_id",
    //   key: "checkin_id",
    //   width: 168,
    //   ellipsis: { showTitle: false },
    //   render: (_, r) => {
    //     const id = r.checkin_id || r.code;
    //     if (!id) return "—";
    //     return (
    //       <Tooltip title={id}>
    //         <Text ellipsis style={{ maxWidth: 156 }} code>
    //           {id}
    //         </Text>
    //       </Tooltip>
    //     );
    //   },
    // },
    {
      title: "Patient",
      key: "patient",
      width: 140,
      ellipsis: true,
      render: (_, r) => {
        const c = r.consumer || {};
        const name = [c.surname || r.surname, c.first_name || r.first_name].filter(Boolean).join(", ").trim();
        return name || "—";
      },
    },
    {
      title: "Insurance No.",
      key: "insurance_no",
      width: 118,
      ellipsis: true,
      render: (_, r) => r.consumer?.insurance_no || r.insurance_no || "—",
    },
    ...(hasOrgInScope
      ? []
      : [
          {
            title: "Organization",
            dataIndex: "organization_name",
            key: "organization_name",
            width: 160,
            ellipsis: true,
            render: (v) => v || "—",
          },
        ]),
    ...(hasPlanInScope
      ? []
      : [
          {
            title: "Plan",
            dataIndex: "plan_name",
            key: "plan_name",
            width: 120,
            ellipsis: true,
            render: (v, r) => v || r.plan_name || "—",
          },
        ]),
    ...(hasFacilityInScope
      ? []
      : [
          {
            title: "Facility",
            key: "facility_name",
            width: 180,
            ellipsis: { showTitle: false },
            render: (_, r) => {
              const name = r.facility_name || "—";
              const loc = [r.facility_city, r.facility_state].filter(Boolean).join(", ");
              if (!loc) return name;
              return (
                <Tooltip title={loc}>
                  <span>{name}</span>
                </Tooltip>
              );
            },
          },
        ]),
    withServerSorter(
      {
        title: "Check-in",
        key: SORT_BY.CHECKIN_DATE,
        width: 132,
        sortDirections: ["descend", "ascend"],
        filteredValue: dateRange ? [true] : null,
        filterDropdown: dateRangeFilterDropdown({
          value: dateRange,
          onChange: (dates) => setDateRange(dates),
        }),
        render: (_, r) => formatEncounterDate(r.checkin_date),
      },
      SORT_BY.CHECKIN_DATE,
      { sort_by: encountersSortBy, sort_dir: encountersSortDir },
    ),
    withServerSorter(
      {
        title: "Check-out",
        key: SORT_BY.CHECKOUT_DATE,
        width: 132,
        sortDirections: ["descend", "ascend"],
        render: (_, r) => formatEncounterDate(r.checkout_date),
      },
      SORT_BY.CHECKOUT_DATE,
      { sort_by: encountersSortBy, sort_dir: encountersSortDir },
    ),
    withServerSorter(
      {
        title: "Age",
        key: SORT_BY.TAT,
        width: 88,
        sortDirections: ["descend", "ascend"],
        render: (_, r) => {
          const tat = tatFromRecord(r);
          if (!tat.label) return "—";
          return (
            <span
              className={`tat-cell${tat.pending ? " tat-cell--pending" : ""}`}
              title={tat.pending ? "Age in queue since received" : "Received to first decision"}
            >
              {tat.label}
            </span>
          );
        },
      },
      SORT_BY.TAT,
      { sort_by: encountersSortBy, sort_dir: encountersSortDir },
    ),
    withServerSorter(
      {
        title: "Items",
        key: SORT_BY.ITEMS,
        width: 88,
        align: "right",
        sortDirections: ["descend", "ascend"],
        filteredValue: rangeFilterValue(encountersColFilters.itemsMin, encountersColFilters.itemsMax),
        filterDropdown: numericRangeFilterDropdown({
          minPlaceholder: "Min items",
          maxPlaceholder: "Max items",
        }),
        render: (_, r) => {
          const filed = r.filed || {};
          const n = Number(filed.line_items_count);
          const counts = r.item_status_counts;
          if (!counts || typeof counts !== "object") {
            return Number.isFinite(n) ? n : "—";
          }
          const tip = (
            <div>
              <div>Approved: {counts.approved ?? 0}</div>
              <div>Pending: {counts.pending ?? 0}</div>
              <div>Queried: {counts.queried ?? 0}</div>
              <div>Rejected: {counts.rejected ?? 0}</div>
            </div>
          );
          return (
            <Tooltip title={tip}>
              <Text>{Number.isFinite(n) ? n : "—"}</Text>
            </Tooltip>
          );
        },
      },
      SORT_BY.ITEMS,
      { sort_by: encountersSortBy, sort_dir: encountersSortDir },
    ),
    withServerSorter(
      {
        title: "Requested",
        key: SORT_BY.REQUESTED,
        width: 110,
        align: "right",
        sortDirections: ["descend", "ascend"],
        filteredValue: rangeFilterValue(encountersColFilters.requestedMin, encountersColFilters.requestedMax),
        filterDropdown: numericRangeFilterDropdown({
          minPlaceholder: "Min ₦",
          maxPlaceholder: "Max ₦",
          prefix: "₦",
        }),
        render: (_, r) => currency((r.filed || {}).total_requested_cost),
      },
      SORT_BY.REQUESTED,
      { sort_by: encountersSortBy, sort_dir: encountersSortDir },
    ),
    withServerSorter(
      {
        title: "Approved",
        key: SORT_BY.APPROVED,
        width: 110,
        align: "right",
        sortDirections: ["descend", "ascend"],
        filteredValue: rangeFilterValue(encountersColFilters.approvedMin, encountersColFilters.approvedMax),
        filterDropdown: numericRangeFilterDropdown({
          minPlaceholder: "Min ₦",
          maxPlaceholder: "Max ₦",
          prefix: "₦",
        }),
        render: (_, r) => currency((r.approved || {}).total_approved_cost),
      },
      SORT_BY.APPROVED,
      { sort_by: encountersSortBy, sort_dir: encountersSortDir },
    ),
    {
      title: "Status",
      dataIndex: "aggregated_status",
      key: "aggregated_status",
      width: 108,
      render: (v) => {
        if (!v) return "—";
        const color = AGGREGATED_STATUS_TAG[v] || "default";
        return <Tag color={color}>{paStatusLabel(v)}</Tag>;
      },
    },
  ];
  return (
    <div className="pa-dashboard">
      <div className="dashboard-page-stack">
        <header className="dash-masthead">
          <div className="dash-masthead__lead">
            <h1 className="dash-masthead__title">Pre-Authorization</h1>
            <p className="dash-masthead__meta">
              {dateRangeLabel ? <span>{dateRangeLabel}</span> : null}
              {dateRangeLabel ? <span className="dash-masthead__sep">·</span> : null}
              <span>Updated {lastUpdated}</span>
            </p>
          </div>
          <div className="dash-masthead__actions">
            <button
              type="button"
              className={`dash-icon-btn ${showFilters ? "is-active" : ""}`}
              onClick={() => setShowFilters((v) => !v)}
              aria-label="Toggle filters"
            >
              <FilterOutlined />
              {hasActiveFilters ? <span className="dash-icon-btn__dot" /> : null}
            </button>
            {hasActiveFilters ? (
              <button type="button" className="dash-icon-btn" onClick={resetFilters} aria-label="Clear filters">
                <ClearOutlined />
              </button>
            ) : null}
            <Button type="default" size="small" onClick={() => navigate(buildPreauthQueuePath())}>
              Process PA
            </Button>
            <Dropdown menu={{ items: downloadMenuItems }} placement="bottomRight" disabled={exporting}>
              <Button
                type="default"
                size="small"
                icon={<DownloadOutlined />}
                loading={exporting}
                aria-label="Download PA analytics"
              >
                Download <DownOutlined />
              </Button>
            </Dropdown>
            <Button
              type="primary"
              size="small"
              icon={<ReloadOutlined />}
              onClick={refreshAll}
              disabled={loading}
              className="dash-masthead__refresh"
            >
              Reload
            </Button>
          </div>
        </header>

        {showFilters ? (
          <div className="dash-filters">
            <Row gutter={[10, 10]} align="bottom">
              <Col xs={24} md={8} lg={6}>
                <span className="pa-filter-label">Time range</span>
                <ConfigurableRangePicker
                  pickerType={filters.interval || DEFAULT_INTERVAL}
                  startOfUnit={filters.interval || DEFAULT_INTERVAL}
                  value={dateRange}
                  onChange={onDateRangeChange}
                />
                {!dateRange ? (
                  <div className="pa-filter-hint">
                    Default: {filters.interval === "day" ? "today" : filters.interval === "week" ? "this week" : filters.interval === "year" ? "this year" : "this month"}
                  </div>
                ) : null}
              </Col>
              <Col xs={12} md={4} lg={3}>
                <span className="pa-filter-label">Interval</span>
                <Select
                  style={{ width: "100%" }}
                  value={filters.interval}
                  onChange={onIntervalChange}
                  options={[
                    { label: "Daily", value: "day" },
                    { label: "Weekly", value: "week" },
                    { label: "Monthly", value: "month" },
                    { label: "Yearly", value: "year" },
                  ]}
                />
              </Col>
              <Col xs={12} md={4} lg={3}>
                <span className="pa-filter-label">Date type</span>
                <Select
                  style={{ width: "100%" }}
                  value={filters.date_type}
                  onChange={onDateTypeChange}
                  options={[
                    { label: "Request date", value: "request_date" },
                    { label: "Approval date", value: "approval_date" },
                  ]}
                />
              </Col>
              <Col xs={12} md={4} lg={3}>
                <span className="pa-filter-label">Line status</span>
                <Select
                  allowClear
                  style={{ width: "100%" }}
                  value={filters.status}
                  onChange={(value) => setFilters((prev) => ({ ...prev, status: value || null }))}
                  placeholder="All"
                  options={[
                    { label: "Approved", value: "Approved" },
                    { label: "Pending (incl. queried)", value: "Pending" },
                    { label: "Queried only", value: "Queried" },
                    { label: "Rejected", value: "Declined" },
                  ]}
                />
              </Col>
              <Col xs={24} md={8} lg={4}>
                <span className="pa-filter-label">Organization</span>
                <DebounceSelect
                  allowClear
                  showSearch
                  labelInValue
                  placeholder="All organizations"
                  fetchOptions={handleOrgSearch}
                  value={orgSelectOption}
                  onChange={(option) => {
                    setOrgSelectOption(option || null);
                    setFilters((prev) => ({
                      ...prev,
                      org_id: option?.value ?? null,
                    }));
                  }}
                  style={{ width: "100%" }}
                />
              </Col>
              <Col xs={24} md={8} lg={4}>
                <span className="pa-filter-label">Plan</span>
                <DebounceSelect
                  allowClear
                  showSearch
                  labelInValue
                  placeholder="All plans"
                  fetchOptions={handlePlanTypeSearch}
                  value={planSelectOption}
                  onChange={(option) => {
                    setPlanSelectOption(option || null);
                    setFilters((prev) => ({ ...prev, plan_type: option?.value ?? null }));
                  }}
                  style={{ width: "100%" }}
                />
              </Col>
              <Col xs={24} md={8} lg={4}>
                <span className="pa-filter-label">Facility</span>
                <DebounceSelect
                  allowClear
                  showSearch
                  labelInValue
                  placeholder="All facilities"
                  fetchOptions={handleFacilitySearch}
                  value={facilitySelectOption}
                  onChange={(option) => {
                    setFacilitySelectOption(option || null);
                    setFilters((prev) => ({ ...prev, location_id: option?.value ?? null }));
                  }}
                  style={{ width: "100%" }}
                />
              </Col>
              <Col xs={24} md={8} lg={4}>
                <span className="pa-filter-label">
                  CS staff{" "}
                  <Tooltip title="Applies to the PA turnaround section only. Other sections are not filtered by staff.">
                    <InfoCircleOutlined />
                  </Tooltip>
                </span>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="All staff (turnaround only)"
                  value={tatStaffId}
                  onChange={(v) => setTatStaffId(v ?? null)}
                  options={tatStaffOptions.map((o) => ({ value: o.id, label: o.name }))}
                  style={{ width: "100%" }}
                  aria-label="Customer Support staff, turnaround section only"
                />
                {tatStaffId ? <div className="pa-filter-hint">Turnaround section only</div> : null}
              </Col>
            </Row>
          </div>
        ) : null}

        {(loading || summary) ? (
          <>
            <FinanceHelpPanel help={financeHelp} />

            <FinanceReconciliationPanel
              reconciliation={reconciliation}
              onNavigateCompare={hasOrgInScope ? openPaymentAdviceCompare : undefined}
              compareLabel="Open utilization reports with equivalent filters"
            />

            {reconciliation?.user_summary ? (
              <p className="finance-reconcile-hint">{reconciliation.user_summary}</p>
            ) : null}

            <DashboardHero metrics={heroMetrics} loading={loading && !summary} />

            {encounterScopeHint ? (
              <p className="pa-encounter-scope-hint">{encounterScopeHint}</p>
            ) : null}

            <div className="pa-insights-grid">
              <DashboardSection
                id="trend"
                icon={<LineChartOutlined />}
                title={getHelpSectionTitle(
                  mergeFinanceHelp(timeSeriesMeta.help, financeHelp),
                  "cost_trend",
                  getHelpSectionTitle(financeHelp, "pa_cost_trend", "PA cost trend"),
                )}
                subtitle="Requested vs approved over time — period total matches snapshot"
                helpText={
                  getHelpSectionTooltip(timeSeriesMeta.help, "cost_trend")
                  || getHelpSectionTooltip(financeHelp, "pa_cost_trend")
                  || getHelpSectionTooltip(financeHelp, "cost_trend")
                  || approvedCostHelp
                }
                collapsible={false}
                variant="content"
              >
                {loadingTimeSeries || (loading && !summary) ? (
                  <RowListSkeleton rows={5} />
                ) : trendBuckets.length > 0 ? (
                  <div className="pa-trend-panel">
                    {trendBuckets.length > 1 ? (
                      <PACostTrendChart
                        labels={trendChartData.labels}
                        requested={trendChartData.requested}
                        approved={trendChartData.approved}
                        pending={trendChartData.pending}
                        rejected={trendChartData.rejected}
                        variance={trendChartData.variance}
                        items={trendChartData.items}
                        encounters={trendChartData.encounters}
                        requestedLabel={getFieldGuideLabel(
                          trendFieldGuide,
                          "total_requested_cost",
                          requestedCostLabel,
                        )}
                        approvedLabel={getFieldGuideLabel(
                          trendFieldGuide,
                          "total_approved_cost",
                          approvedCostLabel,
                        )}
                        formatMoney={currency}
                      />
                    ) : null}

                    {(periodApprovedTotal != null || periodRequestedTotal != null) ? (
                      <div className="pa-trend-row pa-trend-row--total pa-trend-row--sticky">
                        <span className="pa-trend-row__period">Period total</span>
                        <div className="pa-trend-row__values">
                          {periodRequestedTotal != null ? (
                            <span>
                              <FieldGuideLabel
                                label={requestedCostLabel}
                                helpText={requestedCostHelp}
                                howToUse={requestedCostHowTo}
                              />
                              {": "}
                              <strong>{currency(periodRequestedTotal)}</strong>
                            </span>
                          ) : null}
                          {periodApprovedTotal != null ? (
                            <span className="is-approved">
                              <FieldGuideLabel
                                label={approvedCostLabel}
                                helpText={approvedCostHelp}
                                howToUse={approvedCostHowTo}
                                primary
                              />
                              {": "}
                              <strong>{currency(periodApprovedTotal)}</strong>
                            </span>
                          ) : null}
                          <span>Pending: <strong>{currency(summaryData.total_pending_cost)}</strong></span>
                          <span>Rejected: <strong>{currency(summaryData.total_rejected_cost)}</strong></span>
                          <span className="is-variance">Net variance: <strong>{signedMoney(summaryData.variance_net, currency)}</strong></span>
                        </div>
                      </div>
                    ) : null}

                    <Collapse
                      ghost
                      className="pa-trend-breakdown"
                      defaultActiveKey={trendBuckets.length <= 7 ? ["buckets"] : []}
                      items={[
                        {
                          key: "buckets",
                          label: trendBreakdownLabel,
                          children: (
                            <div className="pa-trend-list pa-trend-list--scroll">
                              {trendBuckets.map((bucket) => (
                                <div key={bucket.key} className="pa-trend-row">
                                  <span className="pa-trend-row__period">{bucket.label}</span>
                                  <div className="pa-trend-row__values">
                                    <span>
                                      <FieldGuideLabel
                                        label={getFieldGuideLabel(trendFieldGuide, "total_requested_cost", requestedCostLabel)}
                                        helpText={getFieldGuideHelpText(trendFieldGuide, "total_requested_cost") || requestedCostHelp}
                                        howToUse={getFieldGuideHowToUse(trendFieldGuide, "total_requested_cost") || requestedCostHowTo}
                                      />
                                      {": "}
                                      <strong>{currency(bucket.requested)}</strong>
                                    </span>
                                    <span className="is-approved">
                                      <FieldGuideLabel
                                        label={getFieldGuideLabel(trendFieldGuide, "total_approved_cost", approvedCostLabel)}
                                        helpText={getFieldGuideHelpText(trendFieldGuide, "total_approved_cost") || approvedCostHelp}
                                        howToUse={getFieldGuideHowToUse(trendFieldGuide, "total_approved_cost") || approvedCostHowTo}
                                        primary={isFieldGuidePrimary(trendFieldGuide, "total_approved_cost") || true}
                                      />
                                      {": "}
                                      <strong>{currency(bucket.approved)}</strong>
                                    </span>
                                    <span>Pending: <strong>{currency(bucket.pending)}</strong></span>
                                    <span>Rejected: <strong>{currency(bucket.rejected)}</strong></span>
                                    <span className="is-variance">Variance: <strong>{signedMoney(bucket.variance, currency)}</strong></span>
                                    <span>{bucket.items.toLocaleString()} items · {bucket.encounters} encounters</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ),
                        },
                      ]}
                    />
                  </div>
                ) : (
                  <Empty description="No trend data" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </DashboardSection>

              <DashboardSection
                id="outcomes"
                icon={<PieChartOutlined />}
                title={getHelpSectionTitle(financeHelp, "status_distribution", "Status breakdown")}
                subtitle="PA items by their own status"
                helpText={getHelpSectionTooltip(financeHelp, "status_distribution")}
                collapsible={false}
                variant="content"
              >
                {loading && !summary ? (
                  <RowListSkeleton rows={4} />
                ) : statusBreakdown.totalItems > 0 ? (
                  <div className="pa-status-list">
                    <div className="pa-status-compose">
                      <div
                        className="pa-status-compose__bar"
                        role="img"
                        aria-label={`${statusBreakdown.totalItems} PA items by status`}
                      >
                        {statusBreakdown.parts.map((item) => (
                          <div
                            key={item.status}
                            className={`pa-status-compose__seg pa-status-compose__seg--${item.status}`}
                            style={{
                              flexGrow: Number(item.share_percent) || 0,
                              background: STATUS_BAR_COLORS[item.status] || "#94a3b8",
                            }}
                            title={`${item.label}: ${item.items || 0} items (${percentage(item.share_percent)})`}
                          />
                        ))}
                      </div>
                      <p className="pa-status-compose__total">
                        <strong>{statusBreakdown.totalItems.toLocaleString()}</strong> PA items
                        — each counted once under its own status
                      </p>
                    </div>

                    {statusBreakdown.rows.map((item) => {
                      const isVariance = item.status === "Variance";
                      const drill = item.drilldown;
                      const statusHelp = getStatusLabelHelp(financeHelp, item.status);
                      const tag = (
                        <Tag color={STATUS_BAR_COLORS[item.status]} className="pa-status-row__tag">
                          {item.label}
                        </Tag>
                      );
                      return (
                        <div
                          key={item.status}
                          className={`pa-status-row pa-status-row--${item.status}${isVariance ? " pa-status-row--sub" : ""}${drill ? " is-clickable" : ""}`}
                          onClick={drill ? () => setItemsDrawer(drill) : undefined}
                          onKeyDown={drill ? (e) => e.key === "Enter" && setItemsDrawer(drill) : undefined}
                          role={drill ? "button" : undefined}
                          tabIndex={drill ? 0 : undefined}
                        >
                          <div className="pa-status-row__lead">
                            {statusHelp ? <Tooltip title={statusHelp}>{tag}</Tooltip> : tag}
                            <span className="pa-status-row__meta">
                              {Number(item.items || 0).toLocaleString()} items
                              <span className="pa-status-row__sep">·</span>
                              {percentage(item.share_percent)} {isVariance ? "of approved items" : "of total"}
                            </span>
                          </div>
                          <div className="pa-status-row__amount-block">
                            <span className="pa-status-row__amount-label">
                              <FieldGuideLabel
                                label={STATUS_AMOUNT_LABEL[item.status] || "Amount"}
                                helpText={getFieldGuideHelpText(statusFieldGuide, "amount")}
                              />
                            </span>
                            <span className={`pa-status-row__amount-value pa-status-row__amount-value--${item.status}`}>
                              {isVariance ? signedMoney(item.amount, currency) : currency(item.amount)}
                            </span>
                            {isVariance ? (
                              <span className="pa-status-row__footnote">
                                Reductions {currency(item.reductions)} ({item.reduction_items}) · Increases {currency(item.increases)} ({item.increase_items})
                              </span>
                            ) : item.status === "Pending" && Number(item.queried_items) > 0 ? (
                              <span className="pa-status-row__footnote">
                                Includes {Number(item.queried_items).toLocaleString()} queried
                              </span>
                            ) : null}
                          </div>
                          {drill ? <RightOutlined className="pa-status-row__next" aria-label="View lines" /> : null}
                        </div>
                      );
                    })}

                    {reconciliation?.cost_formula ? (
                      <div className={`pa-status-reconcile${reconciliation.cost_reconciles && reconciliation.items_reconcile ? "" : " is-off"}`}>
                        <span>
                          Approved {currency(summaryData.total_approved_cost)} + Pending {currency(summaryData.total_pending_cost)}
                          {" "}+ Rejected {currency(summaryData.total_rejected_cost)} − Net variance {signedMoney(summaryData.variance_net, currency)}
                          {" "}= <strong>{currency(reconciliation.cost_sum)}</strong>
                        </span>
                        <span>
                          {reconciliation.cost_reconciles && reconciliation.items_reconcile
                            ? `Matches total requested ${currency(summaryData.total_requested_cost)}`
                            : `Does not match total requested ${currency(summaryData.total_requested_cost)}`}
                        </span>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <Empty description="No PA items for these filters" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </DashboardSection>
            </div>

            <PATurnaroundSection
              filterParams={commonParams}
              staffId={tatStaffId}
              staffName={tatStaffOptions.find((o) => o.id === tatStaffId)?.name}
              onStaffOptions={handleTatStaffOptions}
              rangeLabel={dateRangeLabel ? dateRangeLabel : undefined}
            />

            <DashboardSection
              id="queue"
              icon={<UnorderedListOutlined />}
              title={getHelpSectionTitle(financeHelp, "request_queue", "Request queue")}
              subtitle="Breakdown and encounter list"
              helpText={
                getHelpSectionTooltip(financeHelp, "request_queue")
                || getHelpSectionTooltip(breakdownMeta.help, "breakdown")
                || getHelpSectionTooltip(financeHelp, "breakdown")
              }
              collapsible={false}
              variant="content"
              action={
                detailTab === "breakdown" ? (
                  <Segmented
                    size="small"
                    value={breakdownMode}
                    onChange={onBreakdownModeChange}
                    options={[
                      { label: "Organization", value: "organization" },
                      { label: "Plan", value: "plan_type" },
                      { label: "Facility", value: "location" },
                    ]}
                  />
                ) : (
                  <Select
                    size="small"
                    value={encountersScope}
                    style={{ minWidth: 160 }}
                    onChange={(v) => {
                      setEncountersScope(v);
                      setEncountersPage(1);
                    }}
                    options={[
                      { label: "All in scope", value: "in_scope" },
                      { label: "With PA only", value: "with_pa" },
                    ]}
                  />
                )
              }
            >
              {hasQueueContext ? (
                <nav className="pa-queue-context" aria-label="Request queue context">
                  <div className="pa-queue-context__crumbs">
                    {queueContextCrumbs.map((crumb, idx) => {
                      const isLast = idx === queueContextCrumbs.length - 1;
                      return (
                        <React.Fragment key={crumb.key}>
                          {idx > 0 ? <RightOutlined className="pa-queue-context__sep" aria-hidden /> : null}
                          {isLast ? (
                            <span className="pa-context-chip pa-context-chip--current" title={crumb.label}>{crumb.label}</span>
                          ) : (
                            <button
                              type="button"
                              className="pa-context-chip"
                              title={crumb.label}
                              onClick={() => peelQueueContext(crumb.level)}
                            >
                              {crumb.label}
                            </button>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                  {queueContextHint ? (
                    <p className="pa-queue-context__hint">{queueContextHint}</p>
                  ) : null}
                </nav>
              ) : queueContextHint ? (
                <p className="pa-queue-context__hint pa-queue-context__hint--solo">{queueContextHint}</p>
              ) : null}
              <Tabs
                activeKey={detailTab}
                onChange={setDetailTab}
                destroyInactiveTabPane
                items={[
                  {
                    key: "breakdown",
                    label: (
                      <span className="pa-tab-label-with-help">
                        {getHelpSectionTitle(
                          mergeFinanceHelp(breakdownMeta.help, financeHelp),
                          "breakdown",
                          "Cost breakdown",
                        )}
                        {(getHelpSectionTooltip(breakdownMeta.help, "breakdown")
                          || getHelpSectionTooltip(financeHelp, "breakdown")) ? (
                          <Tooltip
                            title={
                              getHelpSectionTooltip(breakdownMeta.help, "breakdown")
                              || getHelpSectionTooltip(financeHelp, "breakdown")
                            }
                          >
                            <InfoCircleOutlined className="pa-tab-help-icon" />
                          </Tooltip>
                        ) : null}
                      </span>
                    ),
                    children: loadingBreakdown || (loading && !summary) ? (
                      <RowListSkeleton rows={5} />
                    ) : breakdown?.length > 0 ? (
                      <div className="pa-trend-list">
                        {breakdown.map((item, idx) => {
                          // Rows the backend could not attribute to this dimension (e.g. self-pay
                          // clients with no organization) are kept so totals reconcile, but there is
                          // no real org/plan/facility id to drill into — so no click, no chevron.
                          const isUnattributed = item.key === "__unattributed__";
                          return (
                          <div
                            key={item.key || idx}
                            className={`pa-breakdown-row${isUnattributed ? " pa-breakdown-row--unattributed" : ""}`}
                            onClick={isUnattributed ? undefined : () => onBreakdownRowClick(item)}
                            onKeyDown={isUnattributed ? undefined : (e) => e.key === "Enter" && onBreakdownRowClick(item)}
                            role={isUnattributed ? undefined : "button"}
                            tabIndex={isUnattributed ? undefined : 0}
                          >
                            <div className="pa-breakdown-row__label">
                              {breakdownMode === "organization" && <BankOutlined />}
                              {breakdownMode === "plan_type" && <ClusterOutlined />}
                              {breakdownMode === "location" && <EnvironmentOutlined />}
                              {item.label || item.name || item.key}
                            </div>
                            <div className="pa-breakdown-row__values">
                              <span>
                                <FieldGuideLabel
                                  label={getFieldGuideLabel(
                                    costBreakdownFieldGuide,
                                    "total_requested_cost",
                                    requestedCostLabel,
                                  )}
                                  helpText={
                                    getFieldGuideHelpText(costBreakdownFieldGuide, "total_requested_cost")
                                    || requestedCostHelp
                                  }
                                  howToUse={
                                    getFieldGuideHowToUse(costBreakdownFieldGuide, "total_requested_cost")
                                    || requestedCostHowTo
                                  }
                                />
                                {": "}
                                <strong>{currency(item.total_requested_cost)}</strong>
                              </span>
                              <span className="is-approved">
                                <FieldGuideLabel
                                  label={getFieldGuideLabel(
                                    costBreakdownFieldGuide,
                                    "total_approved_cost",
                                    approvedCostLabel,
                                  )}
                                  helpText={
                                    getFieldGuideHelpText(costBreakdownFieldGuide, "total_approved_cost")
                                    || approvedCostHelp
                                  }
                                  howToUse={
                                    getFieldGuideHowToUse(costBreakdownFieldGuide, "total_approved_cost")
                                    || approvedCostHowTo
                                  }
                                  primary={
                                    isFieldGuidePrimary(costBreakdownFieldGuide, "total_approved_cost") || true
                                  }
                                />
                                {": "}
                                <strong>{currency(item.total_approved_cost)}</strong>
                              </span>
                              {item.variance_items ? (
                                <span className="is-variance">Variance: <strong>{signedMoney(item.variance_net, currency)}</strong></span>
                              ) : null}
                              <span>{Number(item.total_pa_items || 0).toLocaleString()} items · {item.total_requests || 0} encounters</span>
                            </div>
                            <span className="pa-breakdown-row__next">
                              {!isUnattributed && breakdownDrillHint}
                              {!isUnattributed && <RightOutlined />}
                            </span>
                          </div>
                          );
                        })}
                        {periodApprovedTotal != null ? (
                          <div className="pa-trend-row pa-trend-row--total">
                            <span className="pa-trend-row__period">Period total</span>
                            <div className="pa-trend-row__values">
                              {periodRequestedTotal != null ? (
                                <span>
                                  <FieldGuideLabel
                                    label={requestedCostLabel}
                                    helpText={requestedCostHelp}
                                    howToUse={requestedCostHowTo}
                                  />
                                  {": "}
                                  <strong>{currency(periodRequestedTotal)}</strong>
                                </span>
                              ) : null}
                              <span className="is-approved">
                                <FieldGuideLabel
                                  label={approvedCostLabel}
                                  helpText={approvedCostHelp}
                                  howToUse={approvedCostHowTo}
                                  primary
                                />
                                {": "}
                                <strong>{currency(periodApprovedTotal)}</strong>
                              </span>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <Empty description="No breakdown data" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    ),
                  },
                  {
                    key: "encounters",
                    label: "Encounters",
                    children: encountersLoading && !encounters.length ? (
                      <TableSkeleton rows={6} columns={6} />
                    ) : (
                      <>
                        <p className="dash-group__encounters-hint">
                          Click a row to open PA filing
                          {filters.location_id ? " for this facility slice" : ""}
                        </p>
                        <Table
                          size="small"
                          loading={encountersLoading}
                          columns={encounterColumns}
                          dataSource={encounters}
                          scroll={{ x: encounterColumns.reduce((sum, col) => sum + (Number(col.width) || 0), 0) }}
                          pagination={{
                            current: encountersPage,
                            pageSize: encountersPageSize,
                            total: encountersTotal,
                            showSizeChanger: true,
                            pageSizeOptions: [10, 20, 50],
                            showTotal: (t) => `${t} encounter(s)`,
                          }}
                          onChange={(pagination, tableFilters, sorter) => {
                            const s = Array.isArray(sorter) ? sorter[0] : sorter;
                            const nextSort = sorterToQuery(s);
                            const sortChanged =
                              (nextSort.sort_by || SORT_BY.CHECKIN_DATE) !== encountersSortBy
                              || (nextSort.sort_dir || SORT_DIR.DESC) !== encountersSortDir;
                            const items = parseTableRangeFilter(tableFilters, SORT_BY.ITEMS);
                            const requested = parseTableRangeFilter(tableFilters, SORT_BY.REQUESTED);
                            const approved = parseTableRangeFilter(tableFilters, SORT_BY.APPROVED);
                            const nextFilters = {
                              itemsMin: items.min,
                              itemsMax: items.max,
                              requestedMin: requested.min,
                              requestedMax: requested.max,
                              approvedMin: approved.min,
                              approvedMax: approved.max,
                            };
                            const filtersChanged = JSON.stringify(nextFilters) !== JSON.stringify(encountersColFilters);
                            if (sortChanged) {
                              setEncountersSortBy(nextSort.sort_by || SORT_BY.CHECKIN_DATE);
                              setEncountersSortDir(nextSort.sort_dir || SORT_DIR.DESC);
                            }
                            if (filtersChanged) {
                              setEncountersColFilters(nextFilters);
                            }
                            setEncountersPage(
                              sortChanged || filtersChanged ? 1 : (pagination?.current || 1)
                            );
                            setEncountersPageSize(pagination?.pageSize || encountersPageSize);
                          }}
                          locale={{
                            emptyText: <Empty description="No encounters match filters" />,
                          }}
                          onRow={(record) => ({
                            onClick: () => {
                              if (record.code) {
                                navigate(`/preauth-action?checkin=${encodeURIComponent(record.code)}`, {
                                  state: { item: record, fromPaAnalyticsEncounters: true },
                                });
                              }
                            },
                            style: { cursor: record.code ? "pointer" : "default" },
                          })}
                        />
                      </>
                    ),
                  },
                ]}
              />
            </DashboardSection>
          </>
        ) : null}

        <PAItemsDrawer
          open={Boolean(itemsDrawer)}
          kind={itemsDrawer || "variance"}
          params={commonParams}
          formatMoney={currency}
          onClose={() => setItemsDrawer(null)}
          onOpenEncounter={(line) => {
            if (!line?.checkin_id) return;
            navigate(`/preauth-action?checkin=${encodeURIComponent(line.checkin_id)}`, {
              state: { item: { code: line.checkin_id }, fromPaAnalyticsEncounters: true },
            });
          }}
        />

        {!loading && !summary ? (
          <div className="pa-empty">
            <Empty description="No PA analytics for the selected filters" />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PADashboard;


